import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { business as bizApi, publicApi } from "@/lib/api";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";
import { Save } from "lucide-react";

export default function Settings() {
  const [biz, setBiz] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    bizApi.get().then(({ data }) => setBiz(data || {}));
    publicApi.currencies().then(({ data }) => setCurrencies(data));
  }, []);

  if (!biz) return <div className="p-8 text-sm text-[#64748B]">Loading…</div>;

  const set = (patch) => setBiz({ ...biz, ...patch });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: biz.name,
        tagline: biz.tagline || null,
        email: biz.email || null,
        phone: biz.phone || null,
        website: biz.website || null,
        address: biz.address || null,
        logo_url: biz.logo_url || null,
        primary_currency: biz.primary_currency || "USD",
        default_tax_rate: Number(biz.default_tax_rate) || 0,
        default_terms: biz.default_terms || null,
      };
      const { data } = await bizApi.update(payload);
      setBiz(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={IDS.settings.page} className="p-5 sm:p-8 max-w-3xl mx-auto">
      <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">Business settings</div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-[#001F3F]" style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
        Your brand & defaults
      </h1>

      <div className="mt-8 space-y-6">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-bold tracking-tight text-[#001F3F] mb-4" style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>Business info</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Name</Label><Input value={biz.name || ""} onChange={(e) => set({ name: e.target.value })} className="mt-1.5 h-11 border-slate-200"/></div>
            <div><Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Tagline</Label><Input value={biz.tagline || ""} onChange={(e) => set({ tagline: e.target.value })} className="mt-1.5 h-11 border-slate-200"/></div>
            <div><Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Email</Label><Input value={biz.email || ""} onChange={(e) => set({ email: e.target.value })} className="mt-1.5 h-11 border-slate-200"/></div>
            <div><Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Phone</Label><Input value={biz.phone || ""} onChange={(e) => set({ phone: e.target.value })} className="mt-1.5 h-11 border-slate-200"/></div>
            <div><Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Website</Label><Input value={biz.website || ""} onChange={(e) => set({ website: e.target.value })} className="mt-1.5 h-11 border-slate-200"/></div>
            <div><Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Logo URL</Label><Input value={biz.logo_url || ""} onChange={(e) => set({ logo_url: e.target.value })} className="mt-1.5 h-11 border-slate-200"/></div>
            <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Address</Label><Input value={biz.address || ""} onChange={(e) => set({ address: e.target.value })} className="mt-1.5 h-11 border-slate-200"/></div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-bold tracking-tight text-[#001F3F] mb-4" style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>Defaults</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Primary currency</Label>
              <Select value={biz.primary_currency || "USD"} onValueChange={(v) => set({ primary_currency: v })}>
                <SelectTrigger className="mt-1.5 h-11 border-slate-200"><SelectValue/></SelectTrigger>
                <SelectContent className="max-h-72">
                  {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Default tax rate (fraction)</Label>
              <Input
                type="number" step="0.01"
                value={biz.default_tax_rate ?? 0}
                onChange={(e) => set({ default_tax_rate: e.target.value })}
                className="mt-1.5 h-11 border-slate-200"
              />
              <div className="text-[11px] text-[#64748B] mt-1">e.g. 0.10 for 10%</div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Default terms</Label>
              <Textarea value={biz.default_terms || ""} onChange={(e) => set({ default_terms: e.target.value })} className="mt-1.5 border-slate-200 min-h-[100px]"/>
            </div>
          </div>
        </div>

        <Button
          data-testid={IDS.settings.saveBtn}
          onClick={save} disabled={busy}
          className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 px-6"
        >
          <Save size={16} className="mr-2"/> Save settings
        </Button>
      </div>
    </div>
  );
}
